import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import type { BugSummary } from "@/api/types/BugSummary";
import { BugStatusChip, DueBadge, PriorityBadge, SeverityBadge } from "@/components/BugBadges";
import { UserBadge } from "@/components/Status";
import { Chip } from "@/components/ui";
import { bugUrl } from "@/lib/bugs";
import { relativeTime } from "@/lib/format";
import { appWithFoundIn } from "@/lib/sightings";

/** One bug as a card row: number, severity, priority, title, status, component, apps, client, owner, age. */
export function BugRow({
  bug,
  showProject,
  listSearch,
}: {
  bug: BugSummary;
  showProject?: boolean;
  /** The bug list's filters (`?…`), so the bug page can link back to the same filtered list. */
  listSearch?: string;
}) {
  const { t } = useTranslation();
  return (
    <Link
      to={bugUrl(bug.project_key, bug.number)}
      state={listSearch != null ? { bugsList: listSearch } : undefined}
      className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 text-text no-underline hover:bg-surface-2 hover:text-text hover:no-underline"
    >
      <div className="flex min-w-0 flex-[999_1_320px] flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          {showProject && <span className="font-mono text-xs text-muted">{bug.project_key}</span>}
          <span className="font-mono text-sm font-semibold text-accent">{t("bugs.bugN", { n: bug.number })}</span>
          <SeverityBadge severity={bug.severity} />
          <PriorityBadge priority={bug.priority} />
          <BugStatusChip status={bug.status} resolution={bug.resolution} />
          <DueBadge dueAt={bug.mitigation_due_at} />
          {bug.component && <Chip>{bug.component}</Chip>}
          {bug.apps.map((a) => (
            <Chip key={a.id}>{appWithFoundIn(a, bug.found_in)}</Chip>
          ))}
          {bug.client && (
            <Chip icon="user" tone="outline">
              {bug.client}
            </Chip>
          )}
        </div>
        <span className="truncate font-medium">{bug.title}</span>
      </div>

      <div className="flex w-[160px] flex-col gap-1 text-sm">
        <UserBadge user={bug.owner} fallback={t("bugs.unowned")} />
        {bug.open_action_items > 0 && (
          <span className="text-xs text-muted">{t("bugs.openActions", { count: bug.open_action_items })}</span>
        )}
      </div>

      <span className="w-[120px] text-right text-xs text-muted" title={bug.detected_at}>
        {t("bugs.detected", { time: relativeTime(bug.detected_at) })}
      </span>
    </Link>
  );
}
